import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Zap,
  Code,
  Database,
  Globe,
  Clock,
  Star,
  Award,
} from 'lucide-react';
import { calculateExperience, calculatePeriod } from '../Utility/utils';
import { Experience, TechType } from '../Utility/types';
import { gradientAndTextColors } from '../Constant';

interface ExperienceCardProps {
  experience: Experience;
  isVisible: boolean;
  index: number;
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({
  experience,
  isVisible,
  index,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredTech, setHoveredTech] = useState<string | null>(null);

  // Function to get appropriate icon for technology
  const getTechIcon = (techType: TechType) => {
    switch (techType) {
      case TechType.FRONTEND:
        return <Globe className='w-4 h-4' />;
      case TechType.BACKEND:
        return <Code className='w-4 h-4' />;
      case TechType.DATABASE:
        return <Database className='w-4 h-4' />;
      default:
        return <Zap className='w-4 h-4' />;
    }
  };

  const getGradientAndTextColor = (input: TechType) => {
    switch (input) {
      case TechType.FRONTEND:
        return gradientAndTextColors[0];
      case TechType.BACKEND:
        return gradientAndTextColors[1];
      case TechType.DATABASE:
        return gradientAndTextColors[2];
      default:
        return gradientAndTextColors[3];
    }
  };

  const period = calculatePeriod(experience.startDate, experience.endDate);
  const duration = calculateExperience(
    experience.startDate,
    experience.endDate
  );

  return (
    <div
      className={`transform transition-all duration-700 ease-out ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className='bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-gray-800/70 group'>
        {/* Header Section */}
        <div
          className='p-6 cursor-pointer'
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className='flex items-start gap-4'>
            {/* Company Avatar */}
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${experience.iconBg} shadow-lg`}
            >
              {experience.icon}
            </div>

            {/* Job Info */}
            <div className='flex-1 min-w-0'>
              <div className='flex items-start justify-between gap-4'>
                <div className='min-w-0 flex-1'>
                  <h3 className='text-xl font-semibold text-white group-hover:text-blue-400 transition-colors duration-200'>
                    {experience.title}
                  </h3>
                  <p className='text-gray-300 font-medium mt-1'>
                    {experience.company}
                  </p>
                  <div className='flex flex-col sm:flex-row sm:items-center gap-2 mt-2'>
                    <p className='text-gray-400 text-sm'>{period}</p>
                    {duration && (
                      <>
                        <div className='hidden sm:block w-1 h-1 bg-gray-500 rounded-full'></div>
                        <div className='flex items-center gap-1.5 text-blue-400 text-sm'>
                          <Clock className='w-3.5 h-3.5' />
                          <span className='font-medium'>{duration}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Expand/Collapse Button */}
                <button
                  className='text-gray-400 hover:text-white transition-colors duration-200 p-1 hover:bg-gray-700/50 rounded-lg'
                  aria-label={
                    isExpanded ? 'Collapse details' : 'Expand details'
                  }
                >
                  {isExpanded ? (
                    <ChevronUp className='w-5 h-5 transform transition-transform duration-200' />
                  ) : (
                    <ChevronDown className='w-5 h-5 transform transition-transform duration-200' />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Expandable Content */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='px-6 pb-6'>
            <div className='border-t border-gray-700/50 pt-6'>
              {/* Achievements */}
              <div className='space-y-3 mb-6'>
                {experience.achievements.map((achievement, achIndex) => (
                  <div
                    key={achIndex}
                    className='flex items-start gap-3 group/achievement'
                  >
                    <CheckCircle className='w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5 group-hover/achievement:text-blue-300 transition-colors duration-200' />
                    <p className='text-gray-300 leading-relaxed group-hover/achievement:text-gray-200 transition-colors duration-200'>
                      {achievement}
                    </p>
                  </div>
                ))}
              </div>

              {/* Enhanced Technologies Grid */}
              {experience.technologies && (
                <div className='pt-4 border-t border-gray-700/30'>
                  <div className='flex items-center justify-between mb-6'>
                    <div className='flex items-center gap-3'>
                      <div className='p-2 bg-blue-500/20 rounded-lg'>
                        <Code className='w-5 h-5 text-blue-400' />
                      </div>
                      <div>
                        <h4 className='text-white font-semibold'>
                          Technologies & Tools
                        </h4>
                        <p className='text-gray-400 text-sm'>
                          Core technical expertise
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2 text-xs text-gray-500'>
                      <Award className='w-3 h-3' />
                      <span>{experience.technologies.length} Technologies</span>
                    </div>
                  </div>

                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                    {experience.technologies.map((tech, techIndex) => {
                      const isHovered = hoveredTech === tech.name;

                      return (
                        <div
                          key={techIndex}
                          className={`group/tech relative bg-gradient-to-r ${
                            getGradientAndTextColor(tech.type).gradient
                          } backdrop-blur-sm rounded-xl border transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-pointer overflow-hidden`}
                          onMouseEnter={() => setHoveredTech(tech.name)}
                          onMouseLeave={() => setHoveredTech(null)}
                        >
                          {/* Main Content */}
                          <div className='p-4 relative z-10'>
                            <div className='flex items-center gap-3 mb-3'>
                              <div
                                className={`p-2 bg-white/10 rounded-lg group-hover/tech:bg-white/20 transition-colors duration-200 ${
                                  getGradientAndTextColor(tech.type).textColor
                                }`}
                              >
                                {getTechIcon(tech.type)}
                              </div>
                              <div className='flex-1 min-w-0'>
                                <h5
                                  className={`font-semibold text-sm ${
                                    getGradientAndTextColor(tech.type).textColor
                                  } truncate`}
                                >
                                  {tech.name}
                                </h5>
                                <div className='flex items-center gap-1 mt-1'>
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-2.5 h-2.5 ${
                                        i < tech.proficiency
                                          ? 'text-yellow-400 fill-current'
                                          : 'text-gray-600'
                                      } transition-colors duration-200`}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Proficiency Bar */}
                            <div className='w-full bg-gray-700/50 rounded-full h-1.5 overflow-hidden'>
                              <div
                                className={`h-full bg-gradient-to-r ${getGradientAndTextColor(
                                  tech.type
                                )
                                  .gradient.split(' ')[0]
                                  .replace(
                                    '/20',
                                    '/60'
                                  )} ${getGradientAndTextColor(tech.type)
                                  .gradient.split(' ')[1]
                                  .replace(
                                    '/20',
                                    '/60'
                                  )} transition-all duration-500 ease-out`}
                                style={{
                                  width: `${(tech.proficiency / 5) * 100}%`,
                                  transform: isHovered
                                    ? 'scaleX(1.05)'
                                    : 'scaleX(1)',
                                  transformOrigin: 'left',
                                }}
                              ></div>
                            </div>
                          </div>

                          {/* Hover Effect Overlay */}
                          <div
                            className={`absolute inset-0 bg-gradient-to-r ${
                              getGradientAndTextColor(tech.type).gradient.split(
                                'hover:'
                              )[1]
                            } opacity-0 group-hover/tech:opacity-100 transition-opacity duration-300 pointer-events-none`}
                          ></div>

                          {/* Animated Border */}
                          <div className='absolute inset-0 rounded-xl opacity-0 group-hover/tech:opacity-100 transition-opacity duration-300'>
                            <div
                              className={`absolute inset-0 rounded-xl bg-gradient-to-r ${getGradientAndTextColor(
                                tech.type
                              )
                                .gradient.split('border-')[1]
                                .split(' ')[0]
                                .replace('/30', '/20')} animate-pulse`}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceCard;
